import { createContext, useState, useEffect, useCallback, useContext } from 'react'
import { IProducto } from '../types/producto'
import { getProductos, noRevisar } from '../services/productos.service'
import { INITIAL_ROWS_PER_PAGE } from '../../Products/components/TableDataPagination'
import { borrar, vencer } from '../services/productos.service'
import { handleNewRubro } from '../services/rubros.service'
import { TenantsContext } from '../../../context/tenants.context'
import SnackBarUtils from '../../../utils/SnackBarUtils'
import { defaultPaginatorRes, PaginatorRes } from '../../Pedidos/context/pedidos.context'

interface Filtros {
  pagina: number
  pageSize: number
  codLocal: string
  codRubro: string
  nombreProducto: string
  soloActivos: boolean
  soloARevisar: boolean
  publicadoDesde: string | null
  publicadoHasta: string | null
}

interface Actions {
  borrarProducto: (codProducto: string) => void
  vencerProducto: (codProducto: string) => void
  handleChangeEstado: (codProducto: string, funcion: Function, estado: string) => void
  handleChangeRubro: (codProducto: string, codRubro: string, nombreRubro: string) => void
  handleNoRevisar: (codProducto: string) => void
}

const defaultFiltros: Filtros = {
  pagina: 1,
  pageSize: INITIAL_ROWS_PER_PAGE,
  codLocal: '',
  codRubro: '',
  nombreProducto: '',
  soloActivos: false,
  soloARevisar: false,
  publicadoDesde: null,
  publicadoHasta: null
}

const defaultActions: Actions = {
  borrarProducto: () => {},
  vencerProducto: () => {},
  handleChangeEstado: () => {},
  handleChangeRubro: () => {},
  handleNoRevisar: () => {}
}

interface ProductosAdminContextProps {
  productos: any[]
  productosSeleccionados: any[]
  setProductosSeleccionados: (p: any[]) => void
  puedeVencerVarios: boolean
  puedePausarVarios: boolean
  puedeReanudarVarios: boolean
  accionesMasivas: boolean
  setAccionesMasivas: (accionesMasivas: boolean) => void
  confirmDialog: any
  setConfirmDialog: (confirm: any) => void
  loading: boolean
  paginator: PaginatorRes
  actions: Actions
  searching: boolean
  filtros: Filtros
  setSearching: (searching: boolean) => void
  setFiltros: (filtros: Filtros) => void
  editProductCode: string
  setEditProductCode: (code: string) => void
  codLocalEditing: string
  setCodLocalEditing: (code: string) => void
  clearEditing: () => void
  handleLeft: () => void
  fetchData: () => Promise<void>
}

// create context
export const ProductosAdminContext = createContext<ProductosAdminContextProps>({
  productos: [],
  productosSeleccionados: [],
  setProductosSeleccionados: () => {},
  puedeVencerVarios: false,
  puedePausarVarios: false,
  puedeReanudarVarios: false,
  accionesMasivas: false,
  setAccionesMasivas: () => {},
  confirmDialog: '',
  setConfirmDialog: () => {},
  loading: false,
  paginator: defaultPaginatorRes,
  actions: defaultActions,
  searching: false,
  filtros: defaultFiltros,
  setSearching: () => {},
  setFiltros: () => {},
  editProductCode: '',
  setEditProductCode: () => {},
  codLocalEditing: '',
  setCodLocalEditing: () => {},
  clearEditing: () => {},
  handleLeft: () => {},
  fetchData: async () => {}
})

// create provider
export const ProductosAdminProvider = ({ children }: any) => {
  const [productos, setProductos] = useState<IProducto[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [searching, setSearching] = useState<boolean>(false)
  const [filtros, setFiltros] = useState<Filtros>(defaultFiltros)
  const [paginator, setPaginator] = useState<PaginatorRes>(defaultPaginatorRes)
  const [actions] = useState<Actions>(defaultActions)
  const [editProductCode, setEditProductCode] = useState<string>('')
  const [codLocalEditing, setCodLocalEditing] = useState<string>(null)
  const [productosSeleccionados, setProductosSeleccionados] = useState<any[]>([])
  const [puedePausarVarios, setPuedePausarVarios] = useState<boolean>(false)
  const [puedeReanudarVarios, setPuedeReanudarVarios] = useState<boolean>(false)
  const [puedeVencerVarios, setPuedeVencerVarios] = useState<boolean>(false)
  const [accionesMasivas, setAccionesMasivas] = useState<boolean>(false)
  const [confirmDialog, setConfirmDialog] = useState<any>({
    open: false,
    text: {
      title: '',
      body: '',
      leftButton: 'Cancelar',
      rightButton: 'Confirmar',
      colorButtonConfirm: 'primary'
    },
    action: async () => {}
  })
  const { tenant } = useContext(TenantsContext)

  const fetchData = useCallback(async () => {
    const data = await getProductos({ ...filtros }, tenant).then((res) => res.data)
    setPaginator({
      ...paginator,
      pageNumber: data.pageNumber,
      pageSize: data.pageSize,
      hasNextPage: data.hasNextPage,
      totalRecord: data.totalRecord
    })
    setProductos(data.data)
  }, [filtros])

  const clearEditing = () => {
    setEditProductCode('')
    setCodLocalEditing(null)
  }

  // Paginator
  paginator.handleChangePage = (e: any, newPage: number) => {
    setFiltros((filtros) => ({
      ...filtros,
      pagina: newPage + 1
    }))
  }

  paginator.handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const newPageSize = parseInt(event.target.value, 10)
    setFiltros((filtros) => ({
      ...filtros,
      pageSize: newPageSize,
      pagina: 1
    }))
  }

  // Actions
  actions.handleChangeEstado = async (codProducto: string, funcion: Function, estado: string) => {
    try {
      await funcion(codProducto, tenant).then(() => {
        const newProductos = productos.map((producto) => {
          if (producto.codProducto === codProducto)
            return {
              ...producto,
              aprobado: estado === 'Aprobado',
              rechazado: estado === 'Rechazado',
              pausado: estado === 'Pausado'
            }
          else return producto
        })
        SnackBarUtils.success('Estado actualizado')
        setProductos(newProductos)
      })
    } catch (e) {
      console.log(e)
      SnackBarUtils.error('Error al actualizar el estado')
    }
  }

  actions.handleChangeRubro = async (codProducto: string, codRubro: string, nombreRubro: string) => {
    try {
      const newProductos = productos.map((producto) => {
        if (producto.codProducto === codProducto)
          return {
            ...producto,
            codRubro,
            nombreRubro
          }
        else return producto
      })
      setProductos(newProductos)
      await handleNewRubro(codProducto, codRubro, tenant).then(() => SnackBarUtils.success('Rubro actualizado'))
    } catch (e) {
      console.log(e)
      SnackBarUtils.error('Error al cambiar el rubro')
    }
  }

  actions.borrarProducto = async (codProducto: string) => {
    try {
      setProductos(productos.filter((producto) => producto.codProducto !== codProducto))
      await borrar(codProducto, tenant).then(() => SnackBarUtils.success('Producto eliminado'))
    } catch (e) {
      console.log(e)
    }
  }

  actions.vencerProducto = async (codProducto: string) => {
    try {
      /* setProductos(
        productos.filter((producto) => producto.codProducto !== codProducto)
      ); */
      await vencer([codProducto], tenant).then(() => SnackBarUtils.success('Producto vencido'))
    } catch (e) {
      console.log(e)
    }
  }

  actions.handleNoRevisar = async (codProducto: string) => {
    try {
      await noRevisar(codProducto, tenant).then(() => {
        SnackBarUtils.success(`Se eliminó la etiqueta de revisión del producto ID ${codProducto}`)
        const newProductos = filtros.soloARevisar
          ? productos.filter((producto) => producto.codProducto !== codProducto)
          : productos.map((producto) => {
              if (producto.codProducto === codProducto)
                return {
                  ...producto,
                  infraccionPendiente: false,
                  infracciones: []
                }
              else return producto
            })
        setProductos(newProductos)
      })
    } catch (e) {
      console.log(e)
    }
  }

  useEffect(() => {
    if (!searching) return
    setLoading(true)

    Promise.all([fetchData()])
      .catch((e) => {
        console.log(e)
      })
      .finally(() => {
        setLoading(false)
      })
  }, [filtros, searching, fetchData])

  const handleLeft = useCallback(() => {
    setProductosSeleccionados([])
    setAccionesMasivas(false)
    setPuedePausarVarios(false)
    setPuedeReanudarVarios(false)
    setPuedeVencerVarios(false)
  }, [])

  useEffect(() => {
    if (productosSeleccionados.length <= 0) return setAccionesMasivas(false)

    setAccionesMasivas(true)

    const haySoloPausados = productosSeleccionados.every((p: any) => p.pausado === true)

    const haySoloUnPausados = productosSeleccionados.every((p: any) => p.pausado === false)

    const haySoloVigentes = productosSeleccionados.every((p: any) => p.vencido === false)

    setPuedePausarVarios(!haySoloPausados || haySoloUnPausados)
    setPuedeReanudarVarios(!haySoloUnPausados || haySoloPausados)
    setPuedeVencerVarios(haySoloVigentes)
  }, [productosSeleccionados])

  return (
    <ProductosAdminContext.Provider
      value={{
        productos,
        productosSeleccionados,
        setProductosSeleccionados,
        puedeVencerVarios,
        puedePausarVarios,
        puedeReanudarVarios,
        accionesMasivas,
        setAccionesMasivas,
        confirmDialog,
        setConfirmDialog,
        loading,
        setFiltros,
        paginator,
        actions,
        searching,
        filtros,
        setSearching,
        editProductCode,
        setEditProductCode,
        setCodLocalEditing,
        codLocalEditing,
        clearEditing,
        handleLeft,
        fetchData
      }}
    >
      {children}
    </ProductosAdminContext.Provider>
  )
}
